import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

/**
 * BoardBlockConfigSidebar - admin-facing config panel for a single board
 * report template block (Task 14: manifest-override slot editing).
 *
 * Mirrors WidgetConfigSidebar's shape (`selected*` / `onUpdate` / `onClose`,
 * `isOpen = !!selected*`) but edits the three fields the backend's
 * boardreport.Resolve override understands: title, binding, optional.
 *
 * Binding is edited as a small JSON textarea rather than a bespoke
 * key/value form - the seed bindings are tiny (e.g. `{"horizon": 30}`), so a
 * generic form builder would be over-engineering (YAGNI). On blur, if the
 * text parses, the FULL parsed object is sent via `onUpdate({ binding })` -
 * boardreport.Resolve replaces a block's binding wholesale, never merges it
 * key-by-key, so a partial object here would silently drop the other keys
 * once persisted.
 */
const BoardBlockConfigSidebar = React.memo(
  ({ selectedBlock, onUpdate, onClose }) => {
    const isOpen = !!selectedBlock;
    const [bindingText, setBindingText] = useState("{}");
    const [bindingError, setBindingError] = useState(null);

    // Intentionally re-seeds only on a block-id change, not on every
    // `selectedBlock.binding` change - a binding change while this block is
    // still selected is this same textarea's own blur-commit (via
    // onUpdate), and re-seeding from it would clobber in-progress edits.
    useEffect(() => {
      setBindingText(JSON.stringify(selectedBlock?.binding ?? {}, null, 2));
      setBindingError(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBlock?.id]);

    const handleTitleChange = useCallback(
      (e) => {
        if (onUpdate) onUpdate({ title: e.target.value });
      },
      [onUpdate]
    );

    const handleOptionalChange = useCallback(
      (e) => {
        if (onUpdate) onUpdate({ optional: e.target.checked });
      },
      [onUpdate]
    );

    const handleBindingChange = (e) => setBindingText(e.target.value);

    const handleBindingBlur = () => {
      const trimmed = bindingText.trim();
      let parsed;
      try {
        parsed = JSON.parse(trimmed === "" ? "{}" : trimmed);
      } catch (err) {
        setBindingError("Invalid JSON - not saved.");
        return;
      }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setBindingError("Binding must be a JSON object - not saved.");
        return;
      }
      setBindingError(null);
      if (onUpdate) onUpdate({ binding: parsed });
    };

    return (
      <div
        className={`absolute top-0 bottom-0 bg-card border-l border-card-bd transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{
          right: "0",
          width: "320px",
          zIndex: 1000,
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
        }}
        data-block-settings
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-1 border-b border-card-bd flex-shrink-0">
            <h3 className="text-sm font-medium text-text">Block settings</h3>
            <button
              type="button"
              onClick={() => onClose && onClose()}
              className="p-1 rounded hover:bg-surface-2 transition-colors cursor-pointer"
              aria-label="Close sidebar"
            >
              <FontAwesomeIcon
                icon={icon({ name: "chevrons-right", family: "sharp", style: "light" })}
                className="text-text-2 text-xs"
              />
            </button>
          </div>

          <div className="pt-2 px-4 pb-4 text-left flex-1 overflow-auto space-y-4">
            {!selectedBlock ? (
              <div className="text-sm text-text-2">Select a block to edit its settings.</div>
            ) : (
              <>
                <div>
                  <label htmlFor="block-title" className="block text-xs font-medium text-text-2 mb-1">
                    Title
                  </label>
                  <input
                    id="block-title"
                    type="text"
                    className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
                    value={selectedBlock.title || ""}
                    onChange={handleTitleChange}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="block-optional"
                    type="checkbox"
                    checked={!!selectedBlock.optional}
                    onChange={handleOptionalChange}
                  />
                  <label htmlFor="block-optional" className="text-xs font-medium text-text-2">
                    Optional (may be omitted when empty)
                  </label>
                </div>

                <div>
                  <label htmlFor="block-binding" className="block text-xs font-medium text-text-2 mb-1">
                    Binding (JSON)
                  </label>
                  <textarea
                    id="block-binding"
                    rows={8}
                    className="w-full p-2 border border-border rounded-ctrl text-xs font-mono bg-card text-text"
                    value={bindingText}
                    onChange={handleBindingChange}
                    onBlur={handleBindingBlur}
                  />
                  {bindingError && <div className="text-danger text-xs mt-1">{bindingError}</div>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.selectedBlock?.id !== nextProps.selectedBlock?.id) {
      return false;
    }
    if (prevProps.selectedBlock?.id === undefined && nextProps.selectedBlock?.id === undefined) {
      return true;
    }
    return (
      prevProps.selectedBlock?.title === nextProps.selectedBlock?.title &&
      prevProps.selectedBlock?.optional === nextProps.selectedBlock?.optional &&
      prevProps.selectedBlock?.binding === nextProps.selectedBlock?.binding &&
      prevProps.onUpdate === nextProps.onUpdate &&
      prevProps.onClose === nextProps.onClose
    );
  }
);

BoardBlockConfigSidebar.propTypes = {
  selectedBlock: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    title: PropTypes.string,
    binding: PropTypes.object,
    optional: PropTypes.bool,
  }),
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default BoardBlockConfigSidebar;
